import { useState, useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Bus, MapPin, Navigation, RefreshCw, AlertCircle,
  Square, Clock, Users
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const loadGoogleMaps = () => new Promise((resolve, reject) => {
  if (window.google && window.google.maps) {
    resolve();
    return;
  }

  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    existingScript.addEventListener('load', resolve, { once: true });
    existingScript.addEventListener('error', reject, { once: true });
    return;
  }

  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY || ''}&libraries=marker`;
  script.async = true;
  script.defer = true;
  script.onload = resolve;
  script.onerror = reject;
  document.head.appendChild(script);
});

// Detect vehicle type from bus_id or bus_registration string
const getVehicleType = (busId = '') => {
  const id = (busId || '').toLowerCase();
  if (id.includes('volvo') || id.includes('scania') || id.includes('man') || id.includes('irizar')) return 'coach';
  if (id.includes('mini') || id.includes('combi') || id.includes('hiace')) return 'minibus';
  if (id.includes('car') || id.includes('sedan') || id.includes('suv')) return 'car';
  // Default: large coach bus (most common for Panashe Coaches)
  return 'coach';
};

// Top-down SVG icons for each vehicle type
const VEHICLE_SVGS = {
  coach: (color = '#1e3a5f', accent = '#FFD700') => `
    <svg width="36" height="52" viewBox="0 0 36 52" xmlns="http://www.w3.org/2000/svg">
      <!-- Bus body -->
      <rect x="3" y="6" width="30" height="42" rx="6" ry="6" fill="${color}" stroke="${accent}" stroke-width="2"/>
      <!-- Front windshield -->
      <rect x="6" y="8" width="24" height="10" rx="3" fill="${accent}" opacity="0.85"/>
      <!-- Rear window -->
      <rect x="6" y="38" width="24" height="7" rx="2" fill="${accent}" opacity="0.5"/>
      <!-- Side windows left -->
      <rect x="4" y="22" width="5" height="5" rx="1" fill="${accent}" opacity="0.6"/>
      <rect x="4" y="30" width="5" height="5" rx="1" fill="${accent}" opacity="0.6"/>
      <!-- Side windows right -->
      <rect x="27" y="22" width="5" height="5" rx="1" fill="${accent}" opacity="0.6"/>
      <rect x="27" y="30" width="5" height="5" rx="1" fill="${accent}" opacity="0.6"/>
      <!-- Front headlights -->
      <circle cx="8" cy="7" r="2.5" fill="#fff" opacity="0.9"/>
      <circle cx="28" cy="7" r="2.5" fill="#fff" opacity="0.9"/>
      <!-- Rear lights -->
      <circle cx="8" cy="46" r="2" fill="#ff4444" opacity="0.9"/>
      <circle cx="28" cy="46" r="2" fill="#ff4444" opacity="0.9"/>
      <!-- Direction arrow on roof -->
      <polygon points="18,3 14,10 18,8 22,10" fill="${accent}"/>
    </svg>`,
  minibus: (color = '#7c3aed', accent = '#fbbf24') => `
    <svg width="28" height="42" viewBox="0 0 28 42" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="5" width="24" height="34" rx="5" fill="${color}" stroke="${accent}" stroke-width="1.5"/>
      <rect x="4" y="7" width="20" height="8" rx="2" fill="${accent}" opacity="0.8"/>
      <rect x="4" y="29" width="20" height="6" rx="2" fill="${accent}" opacity="0.4"/>
      <rect x="3" y="18" width="4" height="4" rx="1" fill="${accent}" opacity="0.6"/>
      <rect x="21" y="18" width="4" height="4" rx="1" fill="${accent}" opacity="0.6"/>
      <circle cx="6" cy="6" r="2" fill="#fff" opacity="0.9"/>
      <circle cx="22" cy="6" r="2" fill="#fff" opacity="0.9"/>
      <circle cx="6" cy="37" r="1.5" fill="#ff4444" opacity="0.9"/>
      <circle cx="22" cy="37" r="1.5" fill="#ff4444" opacity="0.9"/>
      <polygon points="14,2 11,8 14,6 17,8" fill="${accent}"/>
    </svg>`,
  car: (color = '#059669', accent = '#d1fae5') => `
    <svg width="22" height="36" viewBox="0 0 22 36" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="6" width="20" height="26" rx="5" fill="${color}" stroke="${accent}" stroke-width="1.5"/>
      <rect x="3" y="8" width="16" height="7" rx="2" fill="${accent}" opacity="0.85"/>
      <rect x="3" y="24" width="16" height="5" rx="2" fill="${accent}" opacity="0.4"/>
      <circle cx="4" cy="7" r="1.5" fill="#fff" opacity="0.9"/>
      <circle cx="18" cy="7" r="1.5" fill="#fff" opacity="0.9"/>
      <circle cx="4" cy="30" r="1.2" fill="#ff4444" opacity="0.9"/>
      <circle cx="18" cy="30" r="1.2" fill="#ff4444" opacity="0.9"/>
      <polygon points="11,2 8.5,7 11,5.5 13.5,7" fill="${accent}"/>
    </svg>`,
};

const VEHICLE_SIZES = {
  coach: [36, 52],
  minibus: [28, 42],
  car: [22, 36],
};

const createVehicleIcon = (busId = '', heading = 0) => {
  const type = getVehicleType(busId);
  const svg = VEHICLE_SVGS[type]();
  const [w, h] = VEHICLE_SIZES[type];

  return L.divIcon({
    className: 'custom-vehicle-marker',
    html: `
      <div style="position:relative; width:${w + 16}px; height:${h + 16}px; display:flex; align-items:center; justify-content:center;">
        <!-- Pulse ring -->
        <div style="
          position:absolute; inset:0; border-radius:50%;
          background: rgba(255,215,0,0.15);
          animation: vehicle-pulse 2s ease-in-out infinite;
        "></div>
        <!-- Rotated vehicle -->
        <div style="transform: rotate(${heading}deg); filter: drop-shadow(0 3px 6px rgba(0,0,0,0.5));">
          ${svg}
        </div>
      </div>
      <style>
        @keyframes vehicle-pulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.4); opacity: 0; }
        }
      </style>`,
    iconSize: [w + 16, h + 16],
    iconAnchor: [(w + 16) / 2, (h + 16) / 2],
    popupAnchor: [0, -(h / 2 + 8)],
  });
};


const GPSTrackingPage = () => {
  const { token } = useAuth();
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [gpsLocations, setGpsLocations] = useState({});
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef({});
  const wsRef = useRef({});
  // Always-fresh trip metadata cache — avoids stale closure in WebSocket handlers
  const tripMetaRef = useRef({});

  // Initialize the map: Google Maps when configured, otherwise OpenStreetMap fallback
  useEffect(() => {
    let cancelled = false;

    const initMap = async () => {
      if (mapRef.current && leafletMapRef.current) return;

      if (GOOGLE_MAPS_API_KEY) {
        try {
          await loadGoogleMaps();
          if (cancelled || !mapRef.current) return;
          const map = new window.google.maps.Map(mapRef.current, {
            center: { lat: -20.1689, lng: 28.5830 },
            zoom: 6,
            mapTypeId: 'roadmap',
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
          });
          leafletMapRef.current = map;
          leafletMapRef.current.__googleMap = true;
          return;
        } catch (e) {
          console.warn('Google Maps failed to load, falling back to OpenStreetMap:', e);
        }
      }

      if (mapRef.current && !leafletMapRef.current) {
        const map = L.map(mapRef.current, {
          center: [-20.1689, 28.5830],
          zoom: 6,
          zoomControl: true,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map);

        leafletMapRef.current = map;
      }
    };

    initMap();

    return () => {
      cancelled = true;
      if (leafletMapRef.current && leafletMapRef.current.__googleMap) {
        leafletMapRef.current = null;
        return;
      }
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, []);

  // Fetch active trips
  useEffect(() => {
    fetchActiveTrips();
    
    return () => {
      // Close all WebSocket connections on unmount
      Object.values(wsRef.current).forEach(ws => ws?.close());
    };
  }, []);

  const fetchActiveTrips = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/trips?status=in_transit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrips(response.data);
      
      // Cache trip metadata in a ref so WebSocket handlers always have fresh data
      response.data.forEach(trip => {
        tripMetaRef.current[trip.id] = trip;
      });
      
      // Connect to GPS for each active trip, passing the trip object directly
      response.data.forEach(trip => {
        connectToTripGPS(trip.id, trip);
      });
    } catch (error) {
      console.error('Failed to fetch trips:', error);
      toast.error('Failed to load active trips');
    } finally {
      setLoading(false);
    }
  };

  const connectToTripGPS = (tripId, tripData) => {
    // Always keep the meta cache up to date
    if (tripData) tripMetaRef.current[tripId] = tripData;

    if (wsRef.current[tripId]) {
      wsRef.current[tripId].close();
    }

    const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProto}//${window.location.host}/ws/gps/${tripId}`;
    const ws = new WebSocket(wsUrl);
    
    ws.onmessage = (event) => {
      const location = JSON.parse(event.data);
      setGpsLocations(prev => ({
        ...prev,
        [tripId]: location
      }));
      // Use the stable ref-based updater so we always have fresh trip data
      updateMarkerFromRef(tripId, location);
    };

    ws.onerror = (error) => {
      console.log(`WebSocket error for trip ${tripId}:`, error);
    };

    wsRef.current[tripId] = ws;
  };

  // Stable marker updater that reads trip metadata from ref, avoiding stale closure
  const updateMarkerFromRef = useCallback((tripId, location) => {
    if (!leafletMapRef.current) return;
    const lat = location.lat;
    const lng = location.lng;
    const trip = tripMetaRef.current[tripId];
    const busId = trip?.bus_id || trip?.bus_registration || '';
    const vehicleType = getVehicleType(busId);
    const titleText = trip?.route_name || `Trip ${tripId.slice(0, 8)}`;
    const driverText = trip?.driver_name ? `<div style="font-size:12px;color:#475569;">🧑‍✈️ ${trip.driver_name}</div>` : '';
    const stopText = location.current_stop ? `<div style="font-size:11px;color:#64748b;margin-top:2px;">📍 ${location.current_stop}</div>` : '';
    const typeLabel = vehicleType === 'coach' ? '🚌 Coach Bus' : vehicleType === 'minibus' ? '🚐 Minibus' : '🚗 Car';
    const popupHtml = `
      <div style="font-family:system-ui,sans-serif;min-width:200px;padding:6px;">
        <div style="font-weight:700;color:#1e3a5f;font-size:14px;border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin-bottom:6px;">${titleText}</div>
        <div style="font-size:12px;color:#64748b;">${typeLabel} &nbsp;|&nbsp; 🆔 ${busId || 'Unknown'}</div>
        ${driverText}
        <div style="font-size:13px;color:#059669;font-weight:600;margin-top:5px;">⚡ ${location.speed || 0} km/h</div>
        ${stopText}
        <div style="font-size:11px;color:#94a3b8;margin-top:4px;">📡 ${lat?.toFixed(5)}°, ${lng?.toFixed(5)}°</div>
      </div>`;

    const isGoogleMap = !!leafletMapRef.current.__googleMap;

    if (isGoogleMap) {
      const markerEntry = markersRef.current[tripId];
      const icon = {
        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 7,
        fillColor: '#1e3a5f',
        fillOpacity: 1,
        strokeColor: '#facc15',
        strokeWeight: 2,
        rotation: location.heading || 0,
      };

      if (markerEntry) {
        markerEntry.setPosition({ lat, lng });
        markerEntry.setIcon(icon);
        markerEntry.infowindow.setContent(popupHtml);
      } else {
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: leafletMapRef.current,
          title: titleText,
          icon,
        });
        const infowindow = new window.google.maps.InfoWindow({ content: popupHtml });
        marker.addListener('click', () => {
          const foundTrip = tripMetaRef.current[tripId];
          if (foundTrip) setSelectedTrip(foundTrip);
          infowindow.open({ anchor: marker, map: leafletMapRef.current });
        });
        marker.infowindow = infowindow;
        markersRef.current[tripId] = marker;
      }
      return;
    }

    const icon = createVehicleIcon(busId, location.heading || 0);
    if (markersRef.current[tripId]) {
      markersRef.current[tripId].setLatLng([lat, lng]);
      markersRef.current[tripId].setIcon(icon);
      markersRef.current[tripId].setPopupContent(popupHtml);
    } else {
      const marker = L.marker([lat, lng], { icon }).addTo(leafletMapRef.current);
      marker.bindPopup(popupHtml);
      marker.on('click', () => {
        const foundTrip = tripMetaRef.current[tripId];
        if (foundTrip) setSelectedTrip(foundTrip);
      });
      markersRef.current[tripId] = marker;
    }
  }, []);



  const centerOnTrip = (tripId) => {
    const location = gpsLocations[tripId];
    if (location && leafletMapRef.current) {
      if (leafletMapRef.current.__googleMap) {
        leafletMapRef.current.panTo({ lat: location.lat, lng: location.lng });
        leafletMapRef.current.setZoom(12);
        const marker = markersRef.current[tripId];
        if (marker && marker.infowindow) {
          marker.infowindow.open({ anchor: marker, map: leafletMapRef.current });
        }
      } else {
        leafletMapRef.current.panTo([location.lat, location.lng]);
        leafletMapRef.current.setZoom(12);
        if (markersRef.current[tripId]) {
          markersRef.current[tripId].openPopup();
        }
      }
    }
    const trip = trips.find(t => t.id === tripId);
    setSelectedTrip(trip);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { color: 'bg-blue-100 text-blue-700', icon: Clock },
      in_transit: { color: 'bg-green-100 text-green-700', icon: Navigation },
      completed: { color: 'bg-slate-100 text-slate-700', icon: Square },
      cancelled: { color: 'bg-red-100 text-red-700', icon: AlertCircle },
    };
    
    const config = statusConfig[status] || statusConfig.scheduled;
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="gps-tracking-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              Real-Time GPS Tracking
            </h1>
            <p className="text-slate-500 mt-1">
              Monitor live bus locations on OpenStreetMap
            </p>
          </div>
          <Button onClick={fetchActiveTrips} variant="outline" data-testid="refresh-tracking-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className="lg:col-span-2">
            <Card className="card-panashe">
              <CardHeader className="border-b">
                <CardTitle className="font-heading flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Live OpenStreetMap
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div 
                  ref={mapRef} 
                  className="h-[500px] w-full z-0"
                  data-testid="gps-map"
                />
              </CardContent>
            </Card>
            
            {/* Selected Trip Info */}
            {selectedTrip && (
              <Card className="card-panashe mt-4">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-panashe-blue rounded-lg flex items-center justify-center">
                        <Bus className="h-6 w-6 text-panashe-gold" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-slate-900">
                          {selectedTrip.route_name || 'Unknown Route'}
                        </h3>
                        <p className="text-sm text-slate-500">
                          Bus: {selectedTrip.bus_registration || selectedTrip.bus_id || 'N/A'}
                        </p>
                      </div>
                    </div>
                    {getStatusBadge(selectedTrip.status)}
                  </div>
                  
                  {gpsLocations[selectedTrip.id] && (
                    <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-sm text-slate-500">Latitude</p>
                        <p className="font-mono font-medium">
                          {gpsLocations[selectedTrip.id].lat?.toFixed(4)}°
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Longitude</p>
                        <p className="font-mono font-medium">
                          {gpsLocations[selectedTrip.id].lng?.toFixed(4)}°
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Speed</p>
                        <p className="font-mono font-medium">
                          {gpsLocations[selectedTrip.id].speed || 0} km/h
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Trip List */}
          <div>
            <Card className="card-panashe">
              <CardHeader className="border-b">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Bus className="h-5 w-5" />
                  Active Trips ({trips.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="spinner mx-auto mb-4" />
                    <p className="text-slate-500">Loading trips...</p>
                  </div>
                ) : trips.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Bus className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No active trips in transit</p>
                  </div>
                ) : (
                  <div className="divide-y max-h-[600px] overflow-y-auto">
                    {trips.map(trip => (
                      <div 
                        key={trip.id}
                        className={`p-4 cursor-pointer transition-colors hover:bg-slate-50 ${
                          selectedTrip?.id === trip.id ? 'bg-panashe-gold/10 border-l-4 border-panashe-gold' : ''
                        }`}
                        onClick={() => centerOnTrip(trip.id)}
                        data-testid={`trip-item-${trip.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900 truncate">
                                {trip.route_name || 'Unknown Route'}
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 mt-1">
                              {trip.bus_registration || trip.bus_id || 'No Bus'}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {trip.departure_datetime ? 
                                format(parseISO(trip.departure_datetime), 'MMM d, HH:mm') : 
                                'N/A'
                              }
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {getStatusBadge(trip.status)}
                            {gpsLocations[trip.id] && (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                Live
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {gpsLocations[trip.id] && (
                          <div className="mt-2 pt-2 border-t text-xs text-slate-500 flex items-center gap-4">
                            <span>{gpsLocations[trip.id].speed || 0} km/h</span>
                            <span className="text-slate-300">|</span>
                            <span>
                              Updated: {gpsLocations[trip.id].timestamp ? 
                                format(parseISO(gpsLocations[trip.id].timestamp), 'HH:mm:ss') : 
                                'N/A'
                              }
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Note */}
        <p className="text-xs text-slate-400 text-center">
          * Powered by OpenStreetMap. Real-time GPS bus tracking telemetry enabled.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default GPSTrackingPage;
